import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShieldCheck,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Download,
  X,
  Layers,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelPolicyMapping = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteMapping, setDeleteMapping] = useState(null);

  // Form State
  const initialForm = {
    company: 'Acme Corp',
    department: 'Sales & Business Development',
    grade: 'All Grades',
    designation: 'All Designations',
    travel_type: 'Domestic Flight',
    minimum_amount: 0,
    maximum_amount: 100000,
    approval_levels: ['Reporting Manager', 'Department Head', 'Travel Desk', 'Finance'],
    auto_approval_enabled: false,
  };

  const [formData, setFormData] = useState(initialForm);
  const [newLevelInput, setNewLevelInput] = useState('');

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/policy-mappings`, { withCredentials: true });
      setMappings(data.mappings || []);
    } catch (err) {
      toast.error('Failed to load approval policy mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async (e) => {
    e.preventDefault();
    if (!formData.approval_levels || formData.approval_levels.length === 0) {
      toast.error('Please configure at least one approval level');
      return;
    }

    try {
      if (editMapping) {
        await axios.put(`${API_URL}/travel-desk/policy-mappings/${editMapping._id}`, formData, { withCredentials: true });
        toast.success('Approval policy mapping updated successfully!');
      } else {
        await axios.post(`${API_URL}/travel-desk/policy-mappings`, formData, { withCredentials: true });
        toast.success('New Approval policy mapping created!');
      }
      setShowCreateModal(false);
      setEditMapping(null);
      setFormData(initialForm);
      fetchMappings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy mapping');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/policy-mappings/${mappingId}`, { withCredentials: true });
      toast.success('Approval mapping deleted');
      setDeleteMapping(null);
      fetchMappings();
    } catch (err) {
      toast.error('Failed to delete approval mapping');
    }
  };

  const addApprovalLevel = () => {
    if (!newLevelInput.trim()) return;
    setFormData({
      ...formData,
      approval_levels: [...formData.approval_levels, newLevelInput.trim()],
    });
    setNewLevelInput('');
  };

  const removeApprovalLevel = (index) => {
    const updated = formData.approval_levels.filter((_, i) => i !== index);
    setFormData({ ...formData, approval_levels: updated });
  };

  const openEdit = (m) => {
    setEditMapping(m);
    setFormData({
      company: m.company || 'Acme Corp',
      department: m.department || 'All Departments',
      grade: m.grade || 'All Grades',
      designation: m.designation || 'All Designations',
      travel_type: m.travel_type || 'Domestic Flight',
      minimum_amount: m.minimum_amount || 0,
      maximum_amount: m.maximum_amount || 100000,
      approval_levels: m.approval_levels || ['Reporting Manager', 'Department Head'],
      auto_approval_enabled: Boolean(m.auto_approval_enabled),
    });
    setShowCreateModal(true);
  };

  const exportCSV = () => {
    const headers = ['Company', 'Department', 'Grade', 'Travel Type', 'Min Amount', 'Max Amount', 'Approval Chain', 'Auto Approval'];
    const rows = filteredMappings.map(m => [
      m.company, m.department, m.grade, m.travel_type,
      m.minimum_amount, m.maximum_amount, `"${(m.approval_levels || []).join(' -> ')}"`,
      m.auto_approval_enabled ? 'Enabled' : 'Disabled'
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Approval_Policy_Mappings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredMappings = mappings.filter((m) => {
    const q = searchTerm.toLowerCase();
    return (
      (m.department || '').toLowerCase().includes(q) ||
      (m.grade || '').toLowerCase().includes(q) ||
      (m.travel_type || '').toLowerCase().includes(q) ||
      (m.company || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Approval Policy Mapping</h1>
            <p className="text-xs text-blue-200">
              Configure multi-level approval workflow chains mapped by Department, Grade, Designation &amp; Travel Type.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={exportCSV}
            className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>

          {hasPermission(user, 'travel_desk.policy_mapping.edit') && (
            <button
              onClick={() => {
                setEditMapping(null);
                setFormData(initialForm);
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> New Mapping
            </button>
          )}
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search department, grade, travel type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchMappings}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex justify-between">
          <span>Active Approval Matrix Mappings ({filteredMappings.length})</span>
          <span className="text-slate-400">Live Database Master</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading approval policy mappings...</div>
        ) : filteredMappings.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">
            No approval mappings found. Click 'New Mapping' to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3.5">Company &amp; Dept</th>
                  <th className="p-3.5">Grade / Designation</th>
                  <th className="p-3.5">Travel Type</th>
                  <th className="p-3.5">Amount Range (₹)</th>
                  <th className="p-3.5">Multi-Level Approval Chain</th>
                  <th className="p-3.5">Auto Approval</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {filteredMappings.map((m) => (
                  <tr key={m._id} className="hover:bg-slate-50">
                    <td className="p-3.5 font-bold text-slate-900">
                      <div>{m.department}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{m.company}</div>
                    </td>
                    <td className="p-3.5 font-semibold text-slate-700">
                      <div>{m.grade}</div>
                      <div className="text-[10px] text-slate-500">{m.designation}</div>
                    </td>
                    <td className="p-3.5 font-bold text-blue-600">{m.travel_type}</td>
                    <td className="p-3.5 font-mono text-emerald-700 font-bold">
                      ₹ {(m.minimum_amount || 0).toLocaleString('en-IN')} – ₹ {(m.maximum_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(m.approval_levels || []).map((c, i) => (
                          <React.Fragment key={i}>
                            <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-[10px] font-bold text-slate-800 shadow-2xs">
                              L{i+1}: {c}
                            </span>
                            {i < (m.approval_levels || []).length - 1 && <ArrowRight className="w-3 h-3 text-slate-400" />}
                          </React.Fragment>
                        ))}
                      </div>
                    </td>
                    <td className="p-3.5">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        m.auto_approval_enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {m.auto_approval_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="p-3.5 text-right space-x-1 shrink-0">
                      {hasPermission(user, 'travel_desk.policy_mapping.edit') && (
                        <button
                          onClick={() => openEdit(m)}
                          className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg font-bold text-[11px]"
                          title="Edit Mapping"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteMapping(m)}
                        className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg font-bold text-[11px]"
                        title="Delete Mapping"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-500" />
                {editMapping ? 'Edit Approval Policy Mapping' : 'Create Approval Policy Mapping'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 font-bold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveMapping} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Grade</label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Travel Type</label>
                  <select
                    value={formData.travel_type}
                    onChange={(e) => setFormData({ ...formData, travel_type: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-bold"
                  >
                    <option value="Domestic Flight">Domestic Flight</option>
                    <option value="International">International</option>
                    <option value="Train / Rail">Train / Rail</option>
                    <option value="Cab / Ground">Cab / Ground</option>
                    <option value="Hotel Stay">Hotel Stay</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Minimum Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.minimum_amount}
                    onChange={(e) => setFormData({ ...formData, minimum_amount: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Maximum Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.maximum_amount}
                    onChange={(e) => setFormData({ ...formData, maximum_amount: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                  />
                </div>
              </div>

              {/* Dynamic Approval Chain Configuration */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <label className="block font-bold text-slate-800 text-xs">
                  Configured Approval Chain Hierarchy (Unlimited Levels)
                </label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {formData.approval_levels.map((lvl, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-white px-2.5 py-1 rounded-lg border border-slate-300 shadow-2xs">
                      <span className="font-bold text-slate-800">L{idx+1}: {lvl}</span>
                      <button
                        type="button"
                        onClick={() => removeApprovalLevel(idx)}
                        className="text-rose-500 font-bold text-xs hover:text-rose-700 ml-1"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="text"
                    placeholder="Add stage e.g. 'Level 3 Vice President'"
                    value={newLevelInput}
                    onChange={(e) => setNewLevelInput(e.target.value)}
                    className="flex-1 h-8 px-3 bg-white border border-slate-300 rounded-lg text-xs outline-none"
                  />
                  <button
                    type="button"
                    onClick={addApprovalLevel}
                    className="px-3 py-1.5 bg-blue-600 text-white font-bold rounded-lg text-xs hover:bg-blue-500"
                  >
                    + Add Level
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_app"
                  checked={formData.auto_approval_enabled}
                  onChange={(e) => setFormData({ ...formData, auto_approval_enabled: e.target.checked })}
                  className="w-4 h-4 rounded text-amber-500"
                />
                <label htmlFor="auto_app" className="font-bold text-slate-700">
                  Enable Auto Approval for this tier
                </label>
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md"
                >
                  {editMapping ? 'Save Mapping Changes' : 'Create Policy Mapping'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE MODAL */}
      {deleteMapping && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-rose-600 text-base">Confirm Delete</h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete approval mapping for <b>{deleteMapping.department}</b>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleteMapping(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Cancel
              </button>
              <button onClick={() => handleDeleteMapping(deleteMapping._id)} className="px-5 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl">
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelPolicyMapping;
